<template>
  <div class="page-container">
    <div class="page-header">
      <picture class="banner-picture">
        <source media="(max-width: 768px)" srcset="@/assets/img/banner/news_m.jpg" />
        <img class="banner-bg" src="@/assets/img/banner/news.jpg" alt="" />
      </picture>
      <div class="banner-overlay"></div>
      <div class="container">
        <h1>新闻资讯</h1>
        <p class="subtitle">关注紫藤检测，了解最新行业动态</p>
      </div>
    </div>
    
    <div class="container content">
      <!-- Categories -->
      <div class="news-categories">
        <div 
          v-for="c in categories" 
          :key="c.key" 
          class="category-item" 
          :class="{ active: activeCategory === c.key }"
          @click="setCategory(c.key)"
        >
          <img class="category-icon" :src="c.icon" :alt="c.label" />
          <span>{{ c.label }}</span>
        </div>
      </div>
      
      <!-- News List -->
      <div class="news-list">
        <div class="news-card" v-for="(item, index) in pagedItems" :key="index">
          <router-link class="news-media" :to="`/news/${item.slug}`">
            <img class="news-cover" :src="item.cover" :alt="item.title" />
          </router-link>
          <div class="news-date">
            <span class="day">{{ item.day }}</span>
            <span class="month">{{ item.month }}</span>
          </div>
          <div class="news-content">
            <div class="news-meta">
              <span class="news-tag">{{ item.category }}</span>
            </div>
            <h3>{{ item.title }}</h3>
            <p>{{ item.excerpt }}</p>
            <router-link class="read-more" :to="`/news/${item.slug}`">查看详情 &rarr;</router-link>
          </div>
        </div>
      </div>
      
      <!-- Pagination -->
      <div class="pagination">
        <span class="page-btn" :class="{ disabled: currentPage === 1 }" @click="prevPage">&lt;</span>
        <span 
          class="page-btn" 
          v-for="p in pages" 
          :key="p" 
          :class="{ active: currentPage === p }"
          @click="goPage(p)"
        >{{ p }}</span>
        <span class="page-btn" :class="{ disabled: currentPage === pageCount }" @click="nextPage">&gt;</span>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue'
export default {
  setup() {
    const { articles } = require('@/data/news.js')
    const companyIcon = require('@/assets/news/company.svg')
    const policyIcon = require('@/assets/news/policy.svg')
    const industryIcon = require('@/assets/news/industry.svg')
    const techIcon = require('@/assets/news/tech.svg')
    const format = (iso) => {
      const d = new Date(iso)
      const day = `${d.getDate()}`.padStart(2, '0')
      const month = `${d.getMonth() + 1}`.padStart(2, '0') + '月'
      const year = `${d.getFullYear()}`
      return { day, month, year }
    }
    const newsItems = articles.map(a => {
      const { day, month, year } = format(a.date)
      return {
        slug: a.slug,
        day,
        month,
        year,
        category: a.category,
        title: a.title,
        excerpt: a.excerpt,
        cover: a.cover
      }
    })
    const categories = [
      { key: '全部', label: '全部资讯', icon: companyIcon },
      { key: '公司新闻', label: '公司新闻', icon: companyIcon },
      { key: '行业动态', label: '行业动态', icon: industryIcon },
      { key: '政策法规', label: '政策法规', icon: policyIcon },
      { key: '技术创新', label: '技术创新', icon: techIcon }
    ]
    const activeCategory = ref('全部')
    const pageSize = 6
    const currentPage = ref(1)
    const filteredItems = computed(() => {
      if (activeCategory.value === '全部') return newsItems
      return newsItems.filter(n => n.category === activeCategory.value)
    })
    const pageCount = computed(() => Math.max(1, Math.ceil(filteredItems.value.length / pageSize)))
    const pages = computed(() => Array.from({ length: pageCount.value }, (_, i) => i + 1))
    const pagedItems = computed(() => {
      const start = (currentPage.value - 1) * pageSize
      return filteredItems.value.slice(start, start + pageSize)
    })
    const setCategory = (key) => {
      activeCategory.value = key
      currentPage.value = 1
    }
    const goPage = (p) => {
      if (p >= 1 && p <= pageCount.value) currentPage.value = p
    }
    const prevPage = () => goPage(currentPage.value - 1)
    const nextPage = () => goPage(currentPage.value + 1)
    return {
      newsItems,
      categories,
      activeCategory,
      setCategory,
      currentPage,
      pageCount,
      pages,
      pagedItems,
      goPage,
      prevPage,
      nextPage
    }
  }
}
</script>

<style scoped>
.page-header {
  padding: 0;
  margin-bottom: 60px;
  color: #fff;
  text-align: center;
  position: relative;
  overflow: hidden;
  aspect-ratio: 4 / 1;
  display: flex;
  align-items: center;
}

.banner-picture {
  position: absolute;
  inset: 0;
  z-index: 0;
  display: block;
}

.banner-bg {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  transform: none;
}

.banner-overlay {
  position: absolute;
  inset: 0;
  opacity: 0.08;
  background:
    radial-gradient(650px 360px at 15% 25%, rgba(255, 255, 255, 0.04) 0%, rgba(255, 255, 255, 0) 62%),
    radial-gradient(620px 360px at 85% 45%, rgba(255, 214, 102, 0.04) 0%, rgba(255, 214, 102, 0) 64%),
    linear-gradient(135deg, rgba(125, 74, 199, 0.40) 0%, rgba(74, 20, 140, 0.34) 100%);
  z-index: 1;
}

.banner-overlay::before {
  content: '';
  position: absolute;
  inset: -25%;
  background:
    radial-gradient(closest-side at 18% 30%, rgba(255, 255, 255, 0.18), rgba(255, 255, 255, 0)),
    radial-gradient(closest-side at 82% 42%, rgba(255, 189, 92, 0.18), rgba(255, 189, 92, 0)),
    radial-gradient(closest-side at 55% 90%, rgba(160, 132, 255, 0.18), rgba(160, 132, 255, 0));
  filter: blur(22px);
  opacity: 0.9;
  animation: pageBannerBlobs 18s ease-in-out infinite alternate;
}

.banner-overlay::after {
  content: '';
  position: absolute;
  inset: 0;
  background:
    radial-gradient(circle at 10px 10px, rgba(255, 255, 255, 0.14) 2px, rgba(255, 255, 255, 0) 3px),
    radial-gradient(circle at 48px 60px, rgba(255, 255, 255, 0.10) 1px, rgba(255, 255, 255, 0) 2px);
  background-size: 70px 70px, 140px 140px;
  opacity: 0.18;
  animation: pageBannerParticles 26s linear infinite;
  pointer-events: none;
}

.page-header .container {
  display: none;
}

.page-header h1 {
  margin: 0 0 15px 0;
  font-size: 2.5rem;
  font-weight: 700;
  animation: pageFadeUp 700ms cubic-bezier(0.2, 0.8, 0.2, 1) both;
}

.subtitle {
  font-size: 1.2rem;
  opacity: 0.9;
  margin: 0;
  letter-spacing: 1px;
  animation: pageFadeUp 800ms 120ms cubic-bezier(0.2, 0.8, 0.2, 1) both;
}

@media (max-width: 768px) {
  .page-header {
    aspect-ratio: 4 / 5;
  }
}

@keyframes pageFadeUp {
  from { opacity: 0; transform: translate3d(0, 10px, 0); }
  to { opacity: 1; transform: translate3d(0, 0, 0); }
}

@keyframes pageBannerZoom {
  from { transform: scale(1.06); }
  to { transform: scale(1.12); }
}

@keyframes pageBannerBlobs {
  0% { transform: translate3d(-1%, -1%, 0) scale(1); }
  50% { transform: translate3d(2%, 2%, 0) scale(1.03); }
  100% { transform: translate3d(-2%, 3%, 0) scale(1.02); }
}

@keyframes pageBannerParticles {
  from { background-position: 0 0, 0 0; }
  to { background-position: 0 -420px, 0 -840px; }
}

@media (prefers-reduced-motion: reduce) {
  .banner-bg,
  .banner-overlay::before,
  .banner-overlay::after,
  .page-header h1,
  .subtitle {
    animation: none !important;
  }
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.content {
  padding-bottom: 80px;
}

/* Categories */
.news-categories {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 16px;
  margin-bottom: 26px;
}

.category-item {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #666;
  padding: 10px 14px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid #eee;
  background: #fff;
  will-change: transform;
  opacity: 0;
  animation: contentFadeUp 650ms 80ms cubic-bezier(0.2, 0.8, 0.2, 1) both;
}

.news-categories .category-item:nth-child(2) { animation-delay: 130ms; }
.news-categories .category-item:nth-child(3) { animation-delay: 180ms; }
.news-categories .category-item:nth-child(4) { animation-delay: 230ms; }
.news-categories .category-item:nth-child(5) { animation-delay: 280ms; }
.news-categories .category-item:nth-child(6) { animation-delay: 330ms; }

.category-item:hover {
  transform: translateY(-2px);
  border-color: #e0d4f5;
}

.category-item.active {
  color: #7D4AC7;
  background: #f8f4fc;
  border-color: #e5d7f8;
}

.category-icon {
  width: 28px;
  height: 28px;
}

/* News List */
.news-list {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.news-card {
  display: flex;
  padding: 18px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 5px 20px rgba(0,0,0,0.05);
  transition: all 0.3s;
  border: 1px solid #f0f0f0;
  will-change: transform;
  opacity: 0;
  animation: contentFadeUp 700ms 160ms cubic-bezier(0.2, 0.8, 0.2, 1) both;
}

.news-list .news-card:nth-child(2) { animation-delay: 220ms; }
.news-list .news-card:nth-child(3) { animation-delay: 280ms; }
.news-list .news-card:nth-child(4) { animation-delay: 340ms; }
.news-list .news-card:nth-child(5) { animation-delay: 400ms; }

.news-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 30px rgba(125, 74, 199, 0.1);
  border-color: #e0d4f5;
}

.news-media {
  width: 180px;
  height: 120px;
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  margin-right: 18px;
  flex-shrink: 0;
  display: block;
}

.news-cover {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transform: scale(1.02);
  transition: transform 0.35s;
  display: block;
}

.news-card:hover .news-cover {
  transform: scale(1.08);
}

.news-date {
  background-color: #f8f4fc;
  color: #7D4AC7;
  width: 90px;
  height: 90px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 12px;
  margin-right: 30px;
  flex-shrink: 0;
  transition: all 0.3s;
}

.news-card:hover .news-date {
  background-color: #7D4AC7;
  color: #fff;
}

.news-date .day {
  font-size: 32px;
  font-weight: 800;
  line-height: 1;
  margin-bottom: 5px;
}

.news-date .month {
  font-size: 14px;
}

.news-content {
  flex: 1;
}

.news-meta {
  margin-bottom: 10px;
}

.news-tag {
  font-size: 12px;
  color: #7D4AC7;
  background-color: #f8f4fc;
  padding: 4px 10px;
  border-radius: 4px;
}

.news-content h3 {
  margin: 0 0 15px 0;
  color: #333;
  font-size: 1.4rem;
  transition: color 0.3s;
}

.news-card:hover .news-content h3 {
  color: #7D4AC7;
}

.news-content p {
  color: #666;
  margin-bottom: 20px;
  line-height: 1.6;
}

.read-more {
  color: #7D4AC7;
  text-decoration: none;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  transition: transform 0.3s;
}

.read-more:hover {
  transform: translateX(5px);
}

/* Pagination */
.pagination {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 30px;
}

.page-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid #e5d7f8;
  color: #7D4AC7;
  cursor: pointer;
  user-select: none;
  padding: 0 10px;
}

.page-btn.active {
  background: #7D4AC7;
  color: #fff;
}

.page-btn.disabled {
  opacity: 0.5;
  pointer-events: none;
}

@keyframes contentFadeUp {
  from { opacity: 0; transform: translate3d(0, 10px, 0); }
  to { opacity: 1; transform: translate3d(0, 0, 0); }
}

@media (prefers-reduced-motion: reduce) {
  .category-item,
  .news-card {
    animation: none !important;
    opacity: 1;
  }
}

@media (max-width: 768px) {
  .news-card {
    flex-direction: column;
    align-items: flex-start;
  }

  .news-media {
    width: 100%;
    height: 190px;
    margin-right: 0;
    margin-bottom: 14px;
  }
  
  .news-date {
    margin-bottom: 20px;
    width: 70px;
    height: 70px;
  }
  
  .news-date .day {
    font-size: 24px;
  }
}
</style>
